import { Menu, Modal } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthTokenExpirationMonitor, useAuth } from '../../hooks/auth';
import { useDirty } from '../Blocker/dirty';
import { setIcon } from '../MapperIcon';
import { SidebarMenuOptions } from './menu';
import { Container } from './styles';

const Sidebar = ({ collapsed = false }) => {
    const navigate = useNavigate();
    const { isDirty, setClean } = useDirty();
    const { canAccessPage, user, updateUserPermission } = useAuth();
    const { t } = useTranslation();
    const location = useLocation();
    const menuOptionData = SidebarMenuOptions();

    const [openKeys, setOpenKeys] = useState(['0']);
    const [rootSubmenuKeys, setRootSubmenuKeys] = useState([]);
    const [menuOptions, setMenuOptions] = useState([...menuOptionData]);

    const tryNavigate = (path, newTab) => {
        if (isDirty) {
            Modal.confirm({
                content: t('Prompt.Leave'),
                onOk: () => {
                    setClean();
                    newTab ? window.open(path, '_blank') : navigate(path);
                },
            });
        } else {
            newTab ? window.open(path, '_blank') : navigate(path);
        }
    };

    useEffect(() => {
        const keys = menuOptions.map((opt) => opt.key);
        setRootSubmenuKeys(keys);
    }, [user.profiles, menuOptions]);

    useEffect(() => {
        if (user.expirationData) {
            const interval = setInterval(async () => {
                await updateUserPermission();
            }, 1000000);
            return () => clearInterval(interval);
        }
    }, [user.expirationData, updateUserPermission]);

    function renderMenuItem(menuOption, showClassStyle) {
        return (
            <Menu.Item
                key={`${menuOption.key}`}
                icon={setIcon(menuOption.icon)}
                className={showClassStyle ? 'menu-item' : 'submenu-item'}
            >
                <a
                    href={menuOption.url}
                    onClick={(e) => {
                        e.preventDefault();
                        tryNavigate(menuOption.url, menuOption.newPage);
                    }}
                >
                    {t(menuOption.name)}
                </a>
            </Menu.Item>
        );
    }

    function hasPermissionConfigRoute(subMenuData) {
        let subMenuPermissions = [];
        let RouteHasPermissionDefine = true;

        subMenuData.forEach((sub) => {
            if (sub.permission.length > 0) {
                sub.permission.forEach((p) => subMenuPermissions.push(p));
            } else {
                RouteHasPermissionDefine = false;
            }
        });

        return { subMenuPermissions, RouteHasPermissionDefine };
    }

    function renderSubMenu(menuOption) {
        // para submenus, mantemos só a checagem canAccessPage/submenus como antes
        const isAdmin = user?.userName?.toLowerCase() === 'admin';
        const { subMenuPermissions, RouteHasPermissionDefine } = hasPermissionConfigRoute(menuOption.subMenu);

        if (RouteHasPermissionDefine && !isAdmin && !canAccessPage(subMenuPermissions)) {
            return null;
        }

        return (
            <Menu.SubMenu key={menuOption.key} icon={setIcon(menuOption.icon)}>
                {mountSubMenuItems(menuOption.subMenu)}
            </Menu.SubMenu>
        );
    }

    function mountMenuItem(menuOption = null, showClassStyle = true) {
        if (!menuOption) return null;
        if (menuOption.name === 'Divider') {
            return (
                <Menu.Divider
                    key={menuOption.key}
                    style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.23)',
                        margin: '0 0 14px',
                    }}
                />
            );
        }

        AuthTokenExpirationMonitor; // apenas para disparar o hook

        const isAdmin = user?.userName?.toLowerCase() === 'admin';
        const isTenantPath = menuOption.url?.toLowerCase() === '/tenant';

        if (menuOption.permission.length > 0) {
            // exceção: admin sempre vê o /tenant
            if (isTenantPath && isAdmin) {
                return renderMenuItem(menuOption, showClassStyle);
            }
            // caso normal: só renderiza se tiver permissão
            if (canAccessPage(menuOption.permission)) {
                return renderMenuItem(menuOption, showClassStyle);
            }
            return null;
        }

        return renderMenuItem(menuOption, showClassStyle);
    }

    function mountSubMenuItems(subMenuItems = []) {
        return subMenuItems.map((sub) => mountMenuItem(sub, false));
    }

    useEffect(() => {
        const stored = localStorage.getItem('Flowy.MenuKey');
        if (stored) setOpenKeys([stored]);
    }, []);

    useEffect(() => {
        if (menuOptions.length > 0) {
            menuOptions.forEach((menu) => {
                if (
                    location.pathname.includes(menu.url) &&
                    openKeys.length > 0 &&
                    openKeys[0] !== menu.key
                ) {
                    localStorage.setItem('Flowy.MenuKey', menu.key);
                }
            });
        }
    }, [openKeys, location.pathname, menuOptions]);

    const handleOpenChange = (keys) => {
        const latest = keys.find((k) => !openKeys.includes(k));
        setOpenKeys(
            rootSubmenuKeys.includes(latest) ? (latest ? [latest] : []) : keys
        );
    };

    return (
        <Container>
            <Menu
                theme="dark"
                mode="inline"
                style={{ width: collapsed ? 55 : '100%' }}
                onClick={(e) => localStorage.setItem('Flowy.MenuKey', e.key)}
                openKeys={openKeys}
                selectedKeys={openKeys}
                defaultOpenKeys={openKeys}
                onOpenChange={handleOpenChange}
                defaultSelectedKeys={openKeys}
            >
                {menuOptions.map((opt) =>
                    opt.subMenu.length > 0 ? renderSubMenu(opt) : mountMenuItem(opt)
                )}
            </Menu>
        </Container>
    );
};

export default Sidebar;
